<template>
  <div class="my-results-page">
    <!-- Hero section -->
    <section class="results-hero">
      <div class="container">
        <div class="hero-content text-center">
          <div class="hero-icon">
            <i class="fas fa-award"></i>
          </div>
          <h1 class="hero-title">
            Félicitations <span v-if="candidate">{{ candidate.first_name }}</span> !
          </h1>
          <p class="hero-subtitle">
            Voici la synthèse coaching de vos résultats aux tests psychométriques.
          </p>
          <p class="hero-job" v-if="application && application.job_title">
            Candidature : <strong>{{ application.job_title }}</strong>
          </p>
        </div>
      </div>
    </section>

    <div class="container py-5">
      <!-- Loading state -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
        <p class="mt-3 text-muted">Chargement de vos résultats...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="alert alert-warning">
        <h5><i class="fas fa-exclamation-triangle me-2"></i>{{ error }}</h5>
        <router-link :to="{ name: 'Home' }" class="btn btn-outline-primary mt-3">
          Retour au tableau de bord
        </router-link>
      </div>

      <!-- Empty -->
      <div v-else-if="!results.length" class="alert alert-info">
        Aucun résultat disponible pour le moment.
      </div>

      <!-- Results -->
      <div v-else>
        <div class="intro-banner mb-5">
          <div class="d-flex align-items-start">
            <div class="intro-icon me-3">
              <i class="fas fa-info-circle"></i>
            </div>
            <div>
              <h5 class="mb-2">À propos de ces résultats</h5>
              <p class="mb-0 text-muted">
                Ces résultats constituent une photographie de votre profil à l'instant T.
                Ils mettent en lumière vos points forts et les axes sur lesquels vous pouvez continuer à progresser.
                Un profil psychométrique <strong>n'est ni bon ni mauvais</strong> : il reflète simplement
                votre manière unique d'aborder les situations.
              </p>
            </div>
          </div>
        </div>

        <!-- Test cards -->
        <div class="row g-4">
          <div
            v-for="(result, index) in results"
            :key="result.test_type + index"
            class="col-12"
          >
            <div class="test-card" :class="'test-' + result.test_type">
              <div class="test-card-header">
                <div class="test-icon">
                  <i :class="getTestIcon(result.test_type)"></i>
                </div>
                <div class="test-title-wrap">
                  <h3 class="test-title">{{ result.test_name }}</h3>
                  <span class="test-subtitle">{{ getTestSubtitle(result.test_type) }}</span>
                </div>
              </div>

              <div class="test-card-body">
                <!-- Chart -->
                <div class="chart-section">
                  <img
                    v-if="getChartUrl(result)"
                    :src="getChartUrl(result)"
                    :alt="'Graphique ' + result.test_name"
                    class="chart-image"
                    loading="lazy"
                  />
                  <div v-else class="chart-placeholder">
                    <i class="fas fa-chart-bar"></i>
                    <p class="text-muted mt-2">Visualisation indisponible</p>
                  </div>
                </div>

                <!-- Levels -->
                <div class="levels-section">
                  <h5 class="section-title">
                    <i class="fas fa-list-check me-2"></i>Vos niveaux par dimension
                  </h5>
                  <div class="levels-grid">
                    <div
                      v-for="level in result.levels"
                      :key="level.factor"
                      class="level-item"
                      :class="'level-' + level.level_class"
                    >
                      <div class="level-info">
                        <span class="level-label">{{ level.label }}</span>
                        <span class="level-value">{{ level.level }}</span>
                      </div>
                      <div class="level-bar">
                        <div
                          class="level-fill"
                          :style="{ width: (level.score * 10) + '%' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Strengths -->
                <div v-if="result.strengths && result.strengths.length" class="highlight-section strengths">
                  <h5 class="section-title">
                    <i class="fas fa-star me-2"></i>Vos points forts
                  </h5>
                  <div class="badge-list">
                    <span
                      v-for="(strength, i) in result.strengths"
                      :key="'s-' + i"
                      class="highlight-badge strength-badge"
                    >
                      <i class="fas fa-check-circle me-1"></i>{{ strength }}
                    </span>
                  </div>
                </div>

                <!-- Development areas -->
                <div v-if="result.development_areas && result.development_areas.length" class="highlight-section development">
                  <h5 class="section-title">
                    <i class="fas fa-seedling me-2"></i>Vos opportunités de développement
                  </h5>
                  <div class="badge-list">
                    <span
                      v-for="(area, i) in result.development_areas"
                      :key="'d-' + i"
                      class="highlight-badge development-badge"
                    >
                      <i class="fas fa-arrow-up me-1"></i>{{ area }}
                    </span>
                  </div>
                </div>

                <!-- Coaching tip -->
                <div class="coaching-tip">
                  <div class="tip-icon">
                    <i class="fas fa-lightbulb"></i>
                  </div>
                  <div class="tip-content">
                    <strong>Conseil coaching</strong>
                    <p class="mb-0">{{ result.coaching_tip }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="footer-actions text-center mt-5">
          <router-link :to="{ name: 'Home' }" class="btn btn-outline-secondary me-2">
            <i class="fas fa-arrow-left me-2"></i>Retour au tableau de bord
          </router-link>
          <router-link :to="{ name: 'Status' }" class="btn btn-primary">
            <i class="fas fa-list me-2"></i>Voir mes candidatures
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MyTestResults',

  data() {
    return {
      loading: true,
      error: null,
      application: null,
      candidate: null,
      results: []
    };
  },

  async created() {
    await this.loadResults();
  },

  methods: {
    async loadResults() {
      const applicationId = this.$route.params.applicationId;
      try {
        const response = await this.$http.get(`/applications/${applicationId}/my-results`);
        this.application = response.data.application;
        this.candidate = response.data.candidate;
        this.results = response.data.results || [];
      } catch (err) {
        const msg = err?.response?.data?.message || err?.response?.data?.error
          || "Une erreur est survenue lors du chargement de vos résultats.";
        this.error = msg;
      } finally {
        this.loading = false;
      }
    },

    getTestIcon(type) {
      const icons = {
        personality: 'fas fa-user-circle',
        behavioral: 'fas fa-users',
        managerial: 'fas fa-briefcase',
        commercial: 'fas fa-handshake',
        motivation: 'fas fa-fire'
      };
      return icons[type] || 'fas fa-chart-line';
    },

    getTestSubtitle(type) {
      const subtitles = {
        personality: 'Big Five - Vos traits de personnalité',
        behavioral: 'DISC - Votre style comportemental',
        managerial: 'Votre potentiel de leadership',
        commercial: 'Vos aptitudes commerciales',
        motivation: 'Ce qui vous fait avancer'
      };
      return subtitles[type] || '';
    },

    getChartUrl(result) {
      if (!result.chart_data) return null;
      try {
        // Visualization services return chart_data in different shapes:
        // - Personality       : { labels, datasets }
        // - Behavioral/Managerial/Motivation : { type, data: {labels, datasets}, options }
        // - Commercial        : { primary_chart: {type, data, options}, secondary_chart: {...} }
        let source = result.chart_data;
        if (source.primary_chart) {
          source = source.primary_chart;
        }

        let chartData;
        let chartType;
        let chartOptions;
        if (source.data && (source.data.labels || source.data.datasets)) {
          chartData = source.data;
          chartType = source.type;
          chartOptions = source.options;
        } else {
          chartData = source;
        }

        const config = result.chart_configuration || {};
        const finalConfig = {
          type: chartType || config.type || 'bar',
          data: chartData,
          options: this.buildSafeOptions(chartOptions || config.options || {})
        };
        const encoded = encodeURIComponent(JSON.stringify(finalConfig));
        return `https://quickchart.io/chart?w=700&h=400&bkg=white&c=${encoded}`;
      } catch (e) {
        return null;
      }
    },

    buildSafeOptions(options) {
      // Keep only QuickChart-safe options (strip function strings to avoid JSON issues / URL bloat)
      const stripFns = (value) => {
        if (Array.isArray(value)) return value.map(stripFns);
        if (value && typeof value === 'object') {
          const out = {};
          for (const k of Object.keys(value)) {
            const v = value[k];
            if (typeof v === 'function') continue;
            if (typeof v === 'string' && v.trim().startsWith('function')) continue;
            out[k] = stripFns(v);
          }
          return out;
        }
        return value;
      };
      const safe = {
        responsive: true,
        maintainAspectRatio: true
      };
      if (options.scales) safe.scales = stripFns(options.scales);
      if (options.plugins) safe.plugins = stripFns(options.plugins);
      return safe;
    }
  }
};
</script>

<style scoped>
.my-results-page {
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
}

/* Hero */
.results-hero {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: white;
  padding: 80px 0 60px;
  text-align: center;
}

.hero-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  color: #ffd700;
}

.hero-title {
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 1.15rem;
  opacity: 0.95;
  margin-bottom: 8px;
}

.hero-job {
  margin-top: 12px;
  font-size: 1rem;
  opacity: 0.9;
}

/* Intro banner */
.intro-banner {
  background: white;
  border-left: 4px solid #2596be;
  padding: 20px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.intro-icon {
  font-size: 1.5rem;
  color: #2596be;
}

/* Test cards */
.test-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  overflow: hidden;
  border: 1px solid #eef0f2;
}

.test-card-header {
  display: flex;
  align-items: center;
  padding: 24px 28px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #eef0f2;
}

.test-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  margin-right: 20px;
  flex-shrink: 0;
}

.test-personality .test-icon { background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%); }
.test-behavioral .test-icon { background: linear-gradient(135deg, #e95d16 0%, #bc6a3c 100%); }
.test-managerial .test-icon { background: linear-gradient(135deg, #6f42c1 0%, #8e5ddd 100%); }
.test-commercial .test-icon { background: linear-gradient(135deg, #20c997 0%, #3dd5a7 100%); }
.test-motivation .test-icon { background: linear-gradient(135deg, #fd7e14 0%, #ffa94d 100%); }

.test-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.test-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}

.test-card-body {
  padding: 28px;
}

/* Chart */
.chart-section {
  text-align: center;
  margin-bottom: 32px;
  padding: 20px;
  background: #fafbfc;
  border-radius: 10px;
}

.chart-image {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.chart-placeholder {
  padding: 40px;
  color: #adb5bd;
  font-size: 2.5rem;
}

/* Sections */
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

/* Levels */
.levels-section {
  margin-bottom: 28px;
}

.levels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
}

.level-item {
  padding: 14px 16px;
  background: #fafbfc;
  border-radius: 8px;
  border-left: 3px solid #dee2e6;
}

.level-item.level-excellent { border-left-color: #28a745; }
.level-item.level-good { border-left-color: #2596be; }
.level-item.level-average { border-left-color: #ffc107; }
.level-item.level-moderate { border-left-color: #fd7e14; }
.level-item.level-low { border-left-color: #e95d16; }

.level-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.level-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.level-value {
  font-size: 0.8rem;
  color: #6c757d;
  background: white;
  padding: 2px 10px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.level-bar {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.level-fill {
  height: 100%;
  background: linear-gradient(90deg, #2596be, #29b3cd);
  border-radius: 3px;
  transition: width 0.6s ease;
}

.level-excellent .level-fill { background: linear-gradient(90deg, #28a745, #5fd180); }
.level-good .level-fill { background: linear-gradient(90deg, #2596be, #29b3cd); }
.level-average .level-fill { background: linear-gradient(90deg, #ffc107, #ffd952); }
.level-moderate .level-fill { background: linear-gradient(90deg, #fd7e14, #ffa94d); }
.level-low .level-fill { background: linear-gradient(90deg, #e95d16, #ff8a4d); }

/* Highlights */
.highlight-section {
  margin-bottom: 24px;
}

.badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.highlight-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.strength-badge {
  background: rgba(40, 167, 69, 0.1);
  color: #1e7e34;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.development-badge {
  background: rgba(233, 93, 22, 0.08);
  color: #bc6a3c;
  border: 1px solid rgba(233, 93, 22, 0.2);
}

/* Coaching tip */
.coaching-tip {
  display: flex;
  align-items: flex-start;
  padding: 18px 20px;
  background: linear-gradient(135deg, #fff3e0 0%, #fff8ed 100%);
  border-radius: 10px;
  border-left: 4px solid #e95d16;
}

.tip-icon {
  width: 40px;
  height: 40px;
  background: #e95d16;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  margin-right: 16px;
  flex-shrink: 0;
}

.tip-content strong {
  display: block;
  color: #2c3e50;
  margin-bottom: 4px;
}

.tip-content p {
  color: #5a6268;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Footer actions */
.footer-actions {
  padding: 40px 0;
}

.footer-actions .btn {
  padding: 12px 28px;
  font-weight: 500;
  border-radius: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #1f7ea0 0%, #2596be 100%);
}

@media (max-width: 768px) {
  .hero-title { font-size: 1.8rem; }
  .test-card-header { padding: 20px; }
  .test-card-body { padding: 20px; }
  .test-icon { width: 48px; height: 48px; font-size: 1.2rem; }
  .test-title { font-size: 1.15rem; }
}
</style>
